import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Heading, 
  Container, 
  SimpleGrid, 
  Icon, 
  useColorModeValue,
  Badge,
  Divider,
  List,
  ListItem,
  ListIcon,
  useToast
} from '@chakra-ui/react'
import { FaCheck, FaCrown, FaStar, FaUsers, FaMicrophone, FaQrcode, FaChartLine, FaArrowLeft, FaCreditCard, FaApplePay, FaLock, FaSync, FaCheckCircle } from 'react-icons/fa'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { pricingService } from '../services/firestore'
import { SubscriptionPlan } from '../types/user'
import { getAuth } from 'firebase/auth'
import { AnalyticsService } from '../services/analytics'
import { useAuthContext } from '../contexts/AuthContext'
import { usePaywallContext } from '../contexts/PaywallContext'

export function PaywallPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const toast = useToast()
  const { isAuthenticated, refreshUserProfile } = useAuthContext()
  const { isSubscriptionActive, userSubscription } = usePaywallContext()
  const [pricingPlans, setPricingPlans] = useState<SubscriptionPlan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [exchangeRate, setExchangeRate] = useState<string>(() => {
    return localStorage.getItem('exchangeRate') || '18.50'
  })
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [showDiscountMsg, setShowDiscountMsg] = useState(false);
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);

  // Check if this is a new registration
  const isNewRegistration = searchParams.get('newRegistration') === 'true'
  
  // Check if user is upgrading (coming from pricing page with plan in localStorage)
  // For upgrades, we need to check if they have a plan selected AND they're not a new registration
  // This includes both active subscribers and expired trial users who want to upgrade
  const isUpgrading = !isNewRegistration && localStorage.getItem('registrationPlanId')
  
  // Determine if user should see trial flow
  // Only show trial flow for new registrations OR users without any plan selection
  const shouldShowTrialFlow = isNewRegistration || !localStorage.getItem('registrationPlanId')

  const bgColor = useColorModeValue('#09090b', '#09090b')
  const textColor = useColorModeValue('white', 'white')
  const cardBg = useColorModeValue('#191919', '#191919')
  const accentColor = '#3182ce'

  useEffect(() => {
    const loadPricingPlans = async () => {
      try {
        const plans = await pricingService.getPricingPlans()
        setPricingPlans(plans)
      } catch (error) {
        console.error('Error loading pricing plans:', error)
        toast({
          title: 'Error',
          description: 'Failed to load pricing information',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadPricingPlans()
  }, [toast])

  // Store the original plan name when component mounts to preserve non-discounted version
  useEffect(() => {
    const originalPlanName = localStorage.getItem('registrationPlanName');
    if (originalPlanName && !localStorage.getItem('registrationOriginalPlanName')) {
      localStorage.setItem('registrationOriginalPlanName', originalPlanName);
    }
  }, [])

  // Prevent browser back button for new registrations
  useEffect(() => {
    if (isNewRegistration) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault()
        e.returnValue = 'You must complete your registration to continue.'
        return 'You must complete your registration to continue.'
      }

      const handlePopState = (e: PopStateEvent) => {
        e.preventDefault()
        toast({
          description: 'Please complete your registration to continue.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        })
        // Push the current state back to prevent navigation
        window.history.pushState(null, '', window.location.href)
      }

      // Add event listeners
      window.addEventListener('beforeunload', handleBeforeUnload)
      window.addEventListener('popstate', handlePopState)

      // Push current state to history
      window.history.pushState(null, '', window.location.href)

      // Cleanup
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload)
        window.removeEventListener('popstate', handlePopState)
      }
    }
  }, [isNewRegistration, toast])

  // Set selected plan from localStorage for both upgrades and new registrations
  useEffect(() => {
    const planId = localStorage.getItem('registrationPlanId');
    const planName = localStorage.getItem('registrationPlanName');
    const originalAmount = localStorage.getItem('registrationOriginalAmount');
    
    if (planId && planName && originalAmount && pricingPlans.length > 0) {
      // Try to find the actual plan from pricing plans first
      const actualPlan = pricingPlans.find(plan => plan.id === planId);
      
      if (actualPlan) {
        // Use the actual plan from pricing plans
        setSelectedPlan(actualPlan);
      } else {
        // Fallback: Create a plan object from localStorage data
        const urlCourts = searchParams.get('courts');
        const planType = planName.toLowerCase().includes('club') ? 'club' : 'individual';
        
        let courts: number | undefined;
        if (planType === 'club') {
          if (urlCourts) {
            courts = parseInt(urlCourts);
          } else {
            // Extract court count from plan name (e.g., "CLUB 2" -> 2)
            const courtMatch = planName.match(/(\d+)/);
            courts = courtMatch ? parseInt(courtMatch[1]) : 1;
          }
        }
        
        const plan: SubscriptionPlan = {
          id: planId,
          name: planName,
          price: Number(originalAmount),
          type: planType,
          description: `${planName} plan`,
          features: [],
          limitations: [],
          courts: courts
        };
        setSelectedPlan(plan);
      }
    }
  }, [isNewRegistration, showDiscountMsg, promoCodeApplied, searchParams, pricingPlans])

  const handleSubscribe = (plan: SubscriptionPlan) => {
    // Navigate to registration with plan selection
    navigate('/auth?plan=' + plan.id)
  }

  const handleBackToLanding = () => {
    // For new registrations, prevent bypassing the paywall
    if (isNewRegistration) {
      toast({
        description: 'Please complete your registration to continue.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      })
      return
    }
    
    // For existing users upgrading, allow going back to pricing
    navigate('/pricing')
  }

  const handleVerifyCard = async () => {
    setIsVerifying(true);
    setVerifyError(null);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('You must be logged in to verify your card.');
      const email = user.email;
      const uid = user.uid;
      // Get plan info from localStorage (as in Phase 6)
      const planId = localStorage.getItem('registrationPlanId') || '';
      const planName = localStorage.getItem('registrationPlanName') || '';
      const promoCode = localStorage.getItem('registrationPromoCode') || undefined;
      const promoCodeId = localStorage.getItem('registrationPromoCodeId') || undefined;
      const discountPercentage = localStorage.getItem('registrationDiscountPercentage') || undefined;
      const originalAmount = localStorage.getItem('registrationOriginalAmount') || undefined;
      
      // Track payment attempt
      const planType = planName.toLowerCase().includes('individual') ? 'individual' : 'club';
      const amount = originalAmount ? parseFloat(originalAmount) : 0;
      await AnalyticsService.logEvent('payment_attempt_started', {
        plan_type: planType,
        amount,
        currency: 'ZAR',
        is_upgrade: isUpgrading,
        has_promo_code: !!promoCode
      });
      
      // For card verification, use tokenizeCardAndStartTrial
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      
      const callFunction = httpsCallable(functions, 'tokenizeCardAndStartTrial');
      
      const result = await callFunction({
        email,
        uid,
        planId,
        planName,
        promoCode,
        promoCodeId,
        isUpgrade: isUpgrading, // Add flag to indicate this is an upgrade
      });
      
      const data = result.data as any;
      if (!data.authorizationUrl) throw new Error('No authorization URL returned from Paystack');
      
      // Track payment redirect
      await AnalyticsService.logEvent('payment_redirect_to_paystack', {
        plan_type: planType,
        amount,
        currency: 'ZAR'
      });
      
      // Redirect to Paystack payment page
      window.location.href = data.authorizationUrl;
    } catch (err: any) {
      // Track payment error
      const planName = localStorage.getItem('registrationPlanName') || '';
      const planType = planName.toLowerCase().includes('individual') ? 'individual' : 'club';
      const originalAmount = localStorage.getItem('registrationOriginalAmount') || '0';
      const amount = parseFloat(originalAmount);
      
      await AnalyticsService.logError('payment_verification_failed', err.message || 'Failed to start card verification.');
      await AnalyticsService.logEvent('payment_attempt_failed', {
        plan_type: planType,
        amount,
        currency: 'ZAR',
        error_message: err.message || 'Failed to start card verification.'
      });
      
      setVerifyError(err.message || 'Failed to start card verification.');
      toast({ title: 'Payment Error', description: err.message || 'Failed to start card verification.', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleContinueWithoutVerifying = async () => {
    // Set loading state for upgrades
    if (isUpgrading) {
      setIsVerifying(true);
    }
    
    try {
      // Track skip verification
      const planName = localStorage.getItem('registrationPlanName') || '';
      const planType = planName.toLowerCase().includes('individual') ? 'individual' : 'club';
      const originalAmount = localStorage.getItem('registrationOriginalAmount') || '0';
      const amount = parseFloat(originalAmount);
      
      await AnalyticsService.logEvent('card_verification_skipped', {
        plan_type: planType,
        amount,
        currency: 'ZAR',
        is_upgrade: isUpgrading
      });
      
      // Import all Firebase functions at the beginning to avoid redeclaration errors
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../config/firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      const { firestore } = await import('../config/firebase');
      
      // If this is an upgrade (not a trial), skip the discount message logic
      if (isUpgrading) {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) throw new Error('You must be logged in to continue.');
        
        // Check if user previously skipped verification
        const userDoc = await getDoc(doc(firestore, 'users', user.uid));
        const skippedVerification = userDoc.data()?.trial?.skippedVerification;
        
        if (!skippedVerification) {
          throw new Error('Invalid upgrade path. Please contact support.');
        }
        
        // Get plan info from localStorage
        const planId = localStorage.getItem('registrationPlanId') || '';
        const planName = localStorage.getItem('registrationPlanName') || '';
        
        // For users who skipped verification, we need to create/get customer and process payment
        let customerCode: string;
        
        // Create/get customer
        const createCustomerFunction = httpsCallable(functions, 'createPaystackCustomer');
        const customerResult = await createCustomerFunction({
          email: user.email,
          uid: user.uid,
          firstName: user.displayName?.split(' ')[0] || '',
          lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
          phone: '',
          metadata: {
            upgrade: true,
            skippedVerification: true
          }
        });
        
        const customerData = customerResult.data as any;
        
        if (!customerData || !customerData.data || (!customerData.data.customer_code && !customerData.data.customer_id)) {
          console.error('Customer creation failed - no customer code found:', customerData);
          throw new Error('Failed to create or retrieve customer');
        }
        
        customerCode = customerData.data.customer_code || customerData.data.customer_id;
        
        // Get the correct Paystack plan code
        const planType = selectedPlan?.type || 'individual';
        const courts = selectedPlan?.courts;
        const promoCode = localStorage.getItem('registrationPromoCode') || undefined;
        const paystackPlanCode = await pricingService.getPaystackPlanCode(planType, courts, promoCode);
        
        // Only use createSubscriptionWithPayment for users who skipped verification
        const createSubscriptionWithPaymentFunction = httpsCallable(functions, 'createSubscriptionWithPayment');
        
        console.log('Creating subscription payment for user who skipped verification - redirecting to payment page');
        
        const subscriptionData = await createSubscriptionWithPaymentFunction({
          customerCode: customerCode,
          planCode: paystackPlanCode,
          email: user.email,
          uid: user.uid,
          planType: selectedPlan?.type || 'individual',
          courts: selectedPlan?.courts || 1,
          skippedVerification: true // Add flag to indicate this is a skipped verification upgrade
        });
        
        console.log('Transaction data for payment redirect:', subscriptionData);
        
        const subscriptionDataResult = subscriptionData.data as any;
        
        // Get the authorization URL for payment
        const paymentUrl = subscriptionDataResult.data?.authorization_url || 
                         subscriptionDataResult.data?.authorizationUrl || 
                         subscriptionDataResult.data?.payment_url ||
                         subscriptionDataResult.data?.paymentUrl ||
                         subscriptionDataResult.data?.url ||
                         subscriptionDataResult.authorization_url || 
                         subscriptionDataResult.authorizationUrl || 
                         subscriptionDataResult.payment_url ||
                         subscriptionDataResult.paymentUrl ||
                         subscriptionDataResult.url;
        
        if (paymentUrl) {
          console.log('Redirecting to payment page:', paymentUrl);
          window.location.href = paymentUrl;
          return; // Exit early since we're redirecting
        } else {
          console.error('No payment URL found in transaction response');
          throw new Error('No payment URL received from Paystack');
        }
      }
      
      // If discount message is already shown, this is the second click - start trial
      if (showDiscountMsg) {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) throw new Error('You must be logged in to continue.');
        
        // Get plan info from localStorage - use original (non-discounted) plan for "Continue without verifying"
        const originalPlanId = localStorage.getItem('registrationOriginalPlanId') || localStorage.getItem('registrationPlanId') || '';
        const originalPlanName = localStorage.getItem('registrationOriginalPlanName') || localStorage.getItem('registrationPlanName') || '';
        // Remove "(DISCOUNTED)" if it exists
        const planName = originalPlanName.replace(' (DISCOUNTED)', '');
        
        // For new users starting trial without card verification
        // Clear any promo code that might have been applied
        localStorage.removeItem('registrationPromoCode');
        localStorage.removeItem('registrationPromoCodeId');
        localStorage.removeItem('registrationDiscountPercentage');
        
        // Create trial subscription without card verification (NO promo code)
        const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
        const { firestore } = await import('../config/firebase');
        
        await setDoc(doc(firestore, 'users', user.uid), {
          trial: {
            status: 'active',
            started: serverTimestamp(),
            trial_ends: new Date(Date.now() + 2592e6), // 30 days (exactly like deployed)
            skippedVerification: true,
            planId: originalPlanId,
            planName: planName, // This will be the non-discounted plan name
            appliedPromoCode: null // NO promo code for "Continue without verifying"
          },
          updatedAt: serverTimestamp()
        }, { merge: true });
        
        // Clear localStorage
        localStorage.removeItem('registrationPlanId');
        localStorage.removeItem('registrationPlanName');
        localStorage.removeItem('registrationOriginalPlanName');
        localStorage.removeItem('registrationPromoCode');
        localStorage.removeItem('registrationPromoCodeId');
        localStorage.removeItem('registrationDiscountPercentage');
        localStorage.removeItem('registrationOriginalAmount');
        
        // Force refresh auth state to pick up email verification
        await refreshUserProfile();
        
        // Redirect to match setup page
        navigate('/match-setup');
        
        toast({
          title: 'Trial Started',
          description: 'Your 30-day free trial has started. Enjoy!',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
          variant: 'solid'
        });
      } else {
        // First click - show discount message and apply BOVRIL if needed
        setShowDiscountMsg(true);
        
        // Check if user already has a promo code applied
        const existingPromoCode = localStorage.getItem('registrationPromoCode');
        
        // If no promo code is applied, apply BOVRIL discount
        if (!existingPromoCode) {
          try {
            // Apply BOVRIL promo code
            const { promoCodeService } = await import('../services/firestore');
            const bovrilPromo = await promoCodeService.getPromoCodeByCode('BOVRIL');
            
            if (bovrilPromo) {
              // Store the BOVRIL promo code in localStorage
              localStorage.setItem('registrationPromoCode', bovrilPromo.code);
              localStorage.setItem('registrationPromoCodeId', bovrilPromo.id || '');
              localStorage.setItem('registrationDiscountPercentage', bovrilPromo.discountPercentage.toString());
              
              // Get original amount from localStorage or use default
              const originalAmount = localStorage.getItem('registrationOriginalAmount') || '4900'; // R49.00 in cents
              localStorage.setItem('registrationOriginalAmount', originalAmount);
              
              // Find the discounted version of the current plan
              const currentPlanId = localStorage.getItem('registrationPlanId');
              if (currentPlanId) {
                // Store the original plan ID before updating to discounted version
                localStorage.setItem('registrationOriginalPlanId', currentPlanId);
                
                const allPlans = await pricingService.getPricingPlans();
                const discountPlan = allPlans.find(plan => 
                  plan.originalPlanId === currentPlanId && plan.isDiscountVersion === true
                );
                
                if (discountPlan) {
                  // Update localStorage with the discounted plan information
                  localStorage.setItem('registrationPlanId', discountPlan.id || '');
                  localStorage.setItem('registrationPlanName', discountPlan.name || '');
                  // Preserve the original plan name (non-discounted version)
                  const originalPlanName = localStorage.getItem('registrationOriginalPlanName') || localStorage.getItem('registrationPlanName') || '';
                  localStorage.setItem('registrationOriginalPlanName', originalPlanName);
                }
              }
              
              // Update state to trigger re-render
              setPromoCodeApplied(true);
              
              toast({
                title: 'Discount Applied',
                description: `Discount (${bovrilPromo.discountPercentage}%) applied successfully!`,
                status: 'success',
                duration: 3000,
                isClosable: true,
              });
            }
          } catch (error) {
            console.error('Error applying BOVRIL promo code:', error);
            // Don't show error toast as this is optional functionality
          }
        }
      }
    } catch (error: any) {
      console.error('Error in handleContinueWithoutVerifying:', error);
      toast({
        title: 'Error',
        description: error.message || 'An error occurred. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      // Reset loading state for upgrades
      if (isUpgrading) {
        setIsVerifying(false);
      }
    }
  };

  return (
    <Box minH="100vh" bg={bgColor} color={textColor} py={16} px={4}>
      {/* Back Arrow */}
      <Box position="absolute" top={4} left="50%" transform="translateX(-50%)" zIndex={9999}>
        <Button
          leftIcon={<FaArrowLeft />}
          onClick={handleBackToLanding}
          variant="ghost"
          color="white"
          _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
          size="lg"
        >
          {/* Hide the text, only show the icon */}
          {/* Back to Pricing */}
        </Button>
      </Box>
      <Container maxW="2xl" w={{ base: "full", md: "52.5%" }}>
        <VStack spacing={8} align="stretch">

          {/* Plan Details */}
          <Box bg={cardBg} borderRadius="lg" p={6} border="1px solid" borderColor="gray.700" mb={4} w="100%">
            <VStack spacing={0} align="stretch">
              <HStack justifyContent="space-between" mb={2}>
                <Text color="gray.400" fontSize="xs">Plan</Text>
                <Text fontSize="sm" color="white">
                  {(() => {
                    // Check if there's a promo code applied
                    const promoCode = localStorage.getItem('registrationPromoCode');
                    
                    if (promoCode) {
                      // Show the discounted plan name from localStorage
                      const discountedPlanName = localStorage.getItem('registrationPlanName');
                      return (discountedPlanName || 'INDIVIDUAL').toUpperCase();
                    } else {
                      // Show the original plan name
                      const originalPlanName = localStorage.getItem('registrationOriginalPlanName');
                      const planName = selectedPlan ? selectedPlan.name : 'INDIVIDUAL';
                      return (originalPlanName || planName).toUpperCase();
                    }
                  })()}
                </Text>
              </HStack>
              <HStack justifyContent="space-between" mb={2}>
                <Text color="gray.400" fontSize="xs">Billing Cycle</Text>
                <Text fontSize="sm" color="white">Monthly</Text>
              </HStack>
              <Box mt={6}>
                <Text color="gray.400" mt={4} fontSize="xs">Payment Breakdown (AFTER trial period)</Text>
                {(() => {
                  const originalAmount = localStorage.getItem('registrationOriginalAmount') || '3900'; // Total amount in cents (R39.00 for individual, R39.00 × courts for club)
                  const totalPrice = (parseInt(originalAmount) / 100).toFixed(2); // Total amount (R39.00 for individual, R740.00 for CLUB 2)
                  const basePrice = (parseInt(originalAmount) / 115).toFixed(2); // VAT exclusive base price
                  const vatAmount = (parseInt(originalAmount) * 0.15 / 115).toFixed(2); // VAT amount
                  
                  if (localStorage.getItem('registrationPromoCode')) {
                    const discountPercentage = localStorage.getItem('registrationDiscountPercentage') || '15';
                    const discountAmount = (parseInt(originalAmount) * parseInt(discountPercentage) / 10000).toFixed(2);
                    const discountedTotal = (parseInt(originalAmount) * (100 - parseInt(discountPercentage)) / 10000).toFixed(2);
                    const discountedVat = (parseInt(originalAmount) * (100 - parseInt(discountPercentage)) * 0.15 / 10000).toFixed(2);
                    
                    return (
                      <>
                        <HStack justify="space-between" mt={2}>
                          <Text color="gray.400" fontSize="xs">Original Price</Text>
                          <Text color="white" fontSize="xs">R {totalPrice}</Text>
                        </HStack>
                        <HStack justify="space-between" mt={2}>
                          <Text color="green.400" fontSize="xs" fontWeight="bold">Promo Code Discount ({discountPercentage}%) per month</Text>
                          <Text color="green.400" fontSize="xs" fontWeight="bold">-R {discountAmount}</Text>
                        </HStack>
                        <HStack justify="space-between" mt={2}>
                          <Text color="gray.400" fontSize="xs" textDecoration="underline" textDecorationStyle="double">Total</Text>
                          <Text color="white" fontSize="xs" textDecoration="underline" textDecorationStyle="double">R {discountedTotal}</Text>
                        </HStack>
                        <HStack justify="space-between" mt={2}>
                          <Text color="gray.400" fontSize="xs" fontStyle="italic">Total includes VAT (15%)</Text>
                          <Text color="white" fontSize="xs" fontStyle="italic">R {discountedVat}</Text>
                        </HStack>
                      </>
                    );
                  } else {
                    return (
                      <>
                        <HStack justify="space-between" mt={2}>
                          <Text color="gray.400" fontSize="xs">Price (excl. VAT)</Text>
                          <Text color="white" fontSize="xs">R {basePrice}</Text>
                        </HStack>
                        <HStack justify="space-between" mt={2}>
                          <Text color="gray.400" fontSize="xs">VAT (15%)</Text>
                          <Text color="white" fontSize="xs">R {vatAmount}</Text>
                        </HStack>
                        <HStack justify="space-between" mt={2}>
                          <Text color="gray.400" fontSize="xs" textDecoration="underline" textDecorationStyle="double">Total</Text>
                          <Text color="white" fontSize="xs" textDecoration="underline" textDecorationStyle="double">R {totalPrice}</Text>
                        </HStack>
                      </>
                    );
                  }
                })()}
              </Box>
            </VStack>
          </Box>
          {/* Secure Payment Info */}
          <Box bg="rgba(49,130,206,0.25)" borderRadius="lg" p={6} border="1px solid" borderColor="gray.700" mb={4}>
            <VStack spacing={4} align="start" textAlign="left">
              <Text fontWeight="bold" color={accentColor} fontSize="sm">Secure Payment with Paystack</Text>
                              <Text color="gray.400" fontSize="xs">Your payment information is encrypted and secured with Paystack. AiS App never store your card details.</Text>
              <Text fontWeight="bold" color={accentColor} fontSize="xs">Accepted Payment Methods</Text>
              <HStack spacing={4}>
                <Box px={4} py={2} border="1px solid #3182ce" borderRadius="0" color="#3182ce" fontWeight="bold" fontSize="xs" bg="transparent">
                  Credit Cards
                </Box>
              </HStack>
            </VStack>
          </Box>
          {/* Action Buttons */}
          <VStack spacing={4}>
            <Button 
              bg="#3182ce"
              color="white"
              _hover={{ bg: "#2c5aa0" }}
              _active={{ bg: "#2c5aa0" }}
              size="lg" 
              leftIcon={<FaCheckCircle />} 
              w="full" 
              fontWeight="bold" 
              onClick={shouldShowTrialFlow ? handleVerifyCard : handleContinueWithoutVerifying} 
              isLoading={isVerifying} 
              loadingText="Redirecting..." 
              fontSize="sm"
              borderRadius="3"
            >
              {shouldShowTrialFlow ? 'Verify Card (R1.50) & Start 30-day Trial' : `Pay R${(() => {
                const originalAmount = localStorage.getItem('registrationOriginalAmount') || '3900';
                return (parseInt(originalAmount) / 100).toFixed(2);
              })()}`}
            </Button>
            <Text color="gray.400" fontSize="sm" textAlign="center" mb={2}>
              {shouldShowTrialFlow 
                ? 'Paystack will verify your card with a once-off R1.50 charge. Your 1-month free trial will start and you\'ll only be billed for a monthly subscription AFTER the trial ends. You can cancel at ANYTIME.'
                : 'Complete your purchase to upgrade your subscription. You can cancel at ANYTIME.'
              }
            </Text>
            {showDiscountMsg && (
              <Text color="white" fontSize="sm" textAlign="center" mb={2}>
                A <Text as="span" color="green.400" fontWeight="bold" display="inline">15% discount</Text> is being applied <Text as="span" color="#3182ce" display="inline">(for 12 months)</Text>, please Verify Card above to lock-in this discount should you choose to continue after your trial. Alternatively, <Text as="span" color="#3182ce" display="inline">forgo any discounts:</Text>
              </Text>
            )}
            {shouldShowTrialFlow && (
              <Button
                variant="ghost"
                color="#3182ce"
                w="full"
                border="1px solid #3182ce"
                borderRadius="3"
                mt={4}
                onClick={handleContinueWithoutVerifying}
                _hover={{ bg: "rgba(49, 130, 206, 0.1)" }}
              >
                Continue without verifying
              </Button>
            )}
            {verifyError && <Text color="red.400" fontSize="sm">{verifyError}</Text>}
          </VStack>
          {/* Terms and Security Badges */}
          <VStack spacing={2} mt={8} textAlign="center">
            <Text color="gray.400" fontSize="sm">
              By completing this purchase, you agree to our{' '}
              <Button variant="link" color="blue.400" onClick={() => navigate('/terms')}>
                Terms of Service
              </Button>{' '}
              and{' '}
              <Button variant="link" color="blue.400" onClick={() => navigate('/terms')}>
                Privacy Policy
              </Button>.
            </Text>
            <HStack spacing={6} color="gray.500" fontSize="xs" justify="center">
                              <Text>🔒 Secure Payment with Paystack</Text>
              <Text>💳 Multiple Payment Methods</Text>
              <Text>🔄 Cancel Anytime</Text>
            </HStack>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
} 